import React, { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Grid,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Shield,
  Bug,
  Container,
  Key,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Scale,
  Gauge,
  Info,
  Tag,
} from 'lucide-react';
import {
  ScoreWidget,
  SectionCard,
  ScoreTrendChart,
  TrendPoint,
  EmptyState,
  KpiCardSkeleton,
  StatusBadge,
} from './DashboardWidgets';
import KpiDetailPanel from './KpiDetailPanel';
import type { ScanProgress } from '@/lib/api/aiKpiService';

// ── Types ─────────────────────────────────────────────────────
interface ToolTabProps {
  toolScore: number | null;
  kpiScores: Record<string, number | string | boolean>;
  topFixes: Array<{ kpi: string; tool: string; action: string; location?: string; effort?: string; impact_points?: number; priority?: string }>;
  trendData?: TrendPoint[];
  loading?: boolean;
  scanning?: boolean;
  scanProgress?: ScanProgress | null;
  pipelineToolStatus?: string;
}

// ── Pipeline Progress ─────────────────────────────────────────
function PipelineProgressState({ toolName, status }: { toolName: string; status?: string }) {
  if (status === 'pending') {
    return (
      <EmptyState
        icon={<Clock size={18} />}
        title={`${toolName} — Queued`}
        description="Waiting to start. Data will appear when the pipeline reaches this tool."
      />
    );
  }
  if (status === 'running') {
    return (
      <EmptyState
        icon={<CircularProgress size={24} thickness={4} />}
        title={`Fetching ${toolName} Data...`}
        description="The pipeline is currently collecting data from this tool. Results will appear shortly."
      />
    );
  }
  if (status === 'failed') {
    return (
      <EmptyState
        icon={<AlertTriangle size={18} />}
        title={`${toolName} — Fetch Failed`}
        description="Failed to fetch data from this tool. It will retry on the next refresh."
      />
    );
  }
  return null;
}

// ── Helpers ───────────────────────────────────────────────────
function formatKpiVal(val: unknown): string {
  if (val == null) return 'N/A';
  if (typeof val === 'boolean') return val ? 'Pass' : 'Fail';
  if (typeof val === 'number') return Number.isInteger(val) ? String(val) : val.toFixed(1);
  return String(val);
}

/**
 * Format the display value shown in a KPI card, using the raw API value when
 * available so the user sees the actual metric (e.g. "0 vulns") instead of
 * the internal 0-100 normalised score.
 *
 * rawVal  — the actual value from the tool report (count, bool, %)
 * normVal — the 0-100 normalised score stored in kpiScores (used as fallback)
 */
function formatRawKpiValue(kpiId: string, rawVal: unknown, normVal: unknown): string {
  // ── Boolean KPIs ────────────────────────────────────────────
  // These are the only ones where normVal (0-100 score) maps cleanly back to
  // a display string without needing the raw value.
  if (kpiId === 'sonar_gate_passed' || kpiId === 'image_scan_pass') {
    const v = rawVal != null ? rawVal : normVal;
    if (v == null) return 'N/A';
    return (v === true || v === 1 || v === 100) ? 'Pass' : 'Fail';
  }
  if (kpiId === 'pipeline_success') {
    const v = rawVal != null ? rawVal : normVal;
    if (v == null) return 'N/A';
    return (v === true || v === 1 || v === 100) ? 'Pass' : 'Fail';
  }
  if (kpiId === 'branch_protected') {
    const v = rawVal != null ? rawVal : normVal;
    if (v == null) return 'N/A';
    return (v === true || v === 1 || v === 100) ? 'Protected' : 'Unprotected';
  }

  // ── Percentage KPIs ─────────────────────────────────────────
  if (kpiId === 'sonar_coverage') {
    // rawVal is the actual % (e.g. 96.4); normVal is the score (also ≈96)
    const v = rawVal != null ? rawVal : normVal;
    if (v == null) return 'N/A';
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return isNaN(n) ? 'N/A' : `${n.toFixed(1)}%`;
  }

  // ── Count / numeric KPIs ─────────────────────────────────────
  // For "lower is better" counts (CVEs, hotspots, secrets, stale PRs) the
  // rawVal is the actual count (e.g. "0 vulnerabilities") while normVal is a
  // 0-100 score that does NOT translate back to the count.  Only display the
  // raw count; if it is unavailable show '—' so the badge colour still gives
  // feedback without showing a misleading number like "100 vulnerabilities".
  if (rawVal == null) return normVal != null ? '—' : 'N/A';

  const val = rawVal;
  if (typeof val === 'number') {
    return Number.isInteger(val) ? String(val) : val.toFixed(1);
  }

  return String(val);
}

// ── SonarQube Tab ─────────────────────────────────────────────
interface SonarQubeTabProps extends ToolTabProps {
  toolReport?: any;
}

export function SonarQubeTab({ toolScore, kpiScores, topFixes, trendData, loading, toolReport, pipelineToolStatus }: SonarQubeTabProps) {
  const theme = useTheme();
  const [activeKpiFilter, setActiveKpiFilter] = useState<string | null>(null);
  if (loading) return <TabSkeleton />;

  // ── KPI definitions ──────────────────────────────────────
  const kpiDefs = [
    { id: 'sonar_gate_passed', label: 'Quality Gate', icon: <CheckCircle2 size={14} /> },
    { id: 'sonar_vulns', label: 'Vulnerabilities', icon: <AlertTriangle size={14} /> },
    { id: 'sonar_hotspots', label: 'Security Hotspots', icon: <Shield size={14} /> },
    { id: 'sonar_coverage', label: 'Test Coverage', icon: <Gauge size={14} /> },
  ];

  // Extract raw values from tool report for all KPI cards and widget rows
  const rawValues: Record<string, any> = {};
  if (toolReport?.projects) {
    for (const p of toolReport.projects) {
      if (!p.found) continue;
      // KPI cards
      if (typeof p.qualityGatePassed === 'boolean') rawValues['sonar_gate_passed'] = p.qualityGatePassed;
      if (p.metrics?.vulnerabilities != null) rawValues['sonar_vulns'] = p.metrics.vulnerabilities;
      if (p.metrics?.securityHotspots != null) rawValues['sonar_hotspots'] = p.metrics.securityHotspots;
      if (p.metrics?.coverage != null) rawValues['sonar_coverage'] = p.metrics.coverage;
      // Widget rows
      if (p.metrics?.codeSmells != null) rawValues['sonar_code_smells'] = p.metrics.codeSmells;
      if (p.metrics?.duplicatedLines != null) rawValues['sonar_duplications'] = p.metrics.duplicatedLines;
      if (p.metrics?.technicalDebt != null) rawValues['sonar_debt'] = p.metrics.technicalDebt;
    }
  }

  // ── Not-Found guard ───────────────────────────────────────────────────────
  // The rawReport has already been filtered to this repo by getToolReportDetail.
  // If every project entry reports found:false any toolScore/kpiScores in state
  // are from a stale previous scan — always show the "not found" empty state
  // regardless of score values, to avoid misleading 0-count "Good" badges.
  const repoProjEntries = toolReport?.projects as any[] | undefined;
  if (repoProjEntries?.length && repoProjEntries.every((p: any) => !p.found)) {
    const notFoundMsg = repoProjEntries[0]?.message || 'No SonarQube project found for this repository.';
    return (
      <EmptyState
        icon={<Bug size={18} />}
        title="SonarQube Project Not Found"
        description={`${notFoundMsg} Add or correct the project mapping in the tool settings.`}
      />
    );
  }
  // ─────────────────────────────────────────────────────────────────────────

  const hasData = (toolScore != null && toolScore > 0) || Object.keys(kpiScores).some(k => k.startsWith('sonar_'));
  if (!hasData) {
    if (pipelineToolStatus === 'running' || pipelineToolStatus === 'pending') {
      return <PipelineProgressState toolName="SonarQube" status={pipelineToolStatus} />;
    }
    if (pipelineToolStatus === 'failed') {
      return <PipelineProgressState toolName="SonarQube" status={pipelineToolStatus} />;
    }
    // Distinguish "repo has no SonarQube project" (tool ran, repo not mapped) from "tool not connected"
    const repoNotMapped = pipelineToolStatus === 'completed' || toolReport?.projects?.length === 0;
    return (
      <EmptyState
        icon={<Bug size={18} />}
        title={repoNotMapped ? 'No SonarQube Project for this Repository' : 'SonarQube Not Connected'}
        description={repoNotMapped
          ? 'This repository has no SonarQube project configured. Switch to another repository to see SonarQube data.'
          : 'No SonarQube data available. Configure it in the onboarding wizard to see KPIs.'}
      />
    );
  }

  // Derive quality gate status from kpiScores
  const gateVal = kpiScores['sonar_gate_passed'];
  const gatePassed = gateVal === true || gateVal === 100 || gateVal === '100';

  // Build filtered issues for the selected KPI
  const filteredFixes = activeKpiFilter
    ? topFixes.filter(f => f.kpi === activeKpiFilter)
    : [];

  // Coverage & duplication values from tool report
  const codeSmellCount = rawValues['sonar_code_smells'] ?? null;
  const dupPct = rawValues['sonar_duplications'] ?? null;
  const debtVal = rawValues['sonar_debt'] ?? null;

  return (
    <Stack spacing={2}>
      {/* ═══════ ROW 1: Score + Quality Gate | Score Trend ═══════ */}
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <SectionCard sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Stack alignItems="center" spacing={1}>
              <ScoreWidget score={toolScore} size={90} label="SonarQube Score" sublabel={toolScore != null ? (toolScore >= 80 ? 'Good' : toolScore >= 60 ? 'Needs Attention' : 'Critical') : undefined} />
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600 }}>Quality Gate:</Typography>
                <StatusBadge status={gatePassed ? 'success' : 'critical'} label={gatePassed ? 'Passed' : 'Failed'} />
              </Stack>
            </Stack>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
          <SectionCard title="Score Trend" icon={<Bug size={14} />} sx={{ flex: 1 }}>
            <ScoreTrendChart data={trendData || []} color="#4caf50" height={120} title="Last 10 scans" />
          </SectionCard>
        </Grid>
      </Grid>

      {/* ═══════ ROW 2: 4 KPI Cards (clickable) ═══════ */}
      <Grid container spacing={2}>
        {kpiDefs.map(kpi => {
          const normScore = kpiScores[kpi.id];
          const score = typeof normScore === 'number' ? normScore : normScore === true ? 100 : normScore === false ? 0 : null;
          const isActive = activeKpiFilter === kpi.id;
          const hasIssues = topFixes.some(f => f.kpi === kpi.id);
          const scoreColor = score != null ? (score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444') : theme.palette.text.disabled;
          // Use raw value from tool report so user sees actual metric (e.g. "0" vulns, "96.4%" coverage)
          const displayStr = formatRawKpiValue(kpi.id, rawValues[kpi.id], normScore);
          return (
            <Grid key={kpi.id} item xs={6} md={3}>
              <Box
                onClick={() => setActiveKpiFilter(isActive ? null : kpi.id)}
                sx={{
                  p: 2, borderRadius: 2,
                  border: '2px solid', borderColor: isActive ? 'primary.main' : 'divider',
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.02) },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Box sx={{ color: scoreColor, display: 'flex' }}>{kpi.icon}</Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>{kpi.label}</Typography>
                </Stack>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: scoreColor }}>{displayStr}</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                  <StatusBadge status={score != null && score >= 80 ? 'success' : score != null && score >= 60 ? 'warning' : 'critical'} label={score != null ? (score >= 80 ? 'Good' : score >= 60 ? 'Warning' : 'Critical') : 'N/A'} />
                  {hasIssues && (
                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>• click for details</Typography>
                  )}
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* ═══════ ROW 3: KPI Detail Panel (only for the 4 main card KPIs) ═══════
           Wrapped in spacing={2} Grid so the panel's content area starts/ends at
           exactly the same x-coordinates as the card content above. */}
      {activeKpiFilter && kpiDefs.some(k => k.id === activeKpiFilter) && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <KpiDetailPanel
              kpiId={activeKpiFilter}
              kpiLabel={kpiDefs.find(k => k.id === activeKpiFilter)?.label || activeKpiFilter}
              value={kpiScores[activeKpiFilter]}
              rawValue={rawValues[activeKpiFilter]}
              topFixes={topFixes}
              onClose={() => setActiveKpiFilter(null)}
            />
          </Grid>
        </Grid>
      )}

      {/* ═══════ ROW 4: Metrics Widgets (clickable — open KPI detail panel) ═══════ */}
      {(() => {
        const metricWidgets = [
          {
            kpiId: 'sonar_code_smells',
            label: 'Code Smells',
            tooltip: 'Maintainability issues detected by SonarQube',
            rawVal: codeSmellCount,
            display: codeSmellCount != null ? String(codeSmellCount) : '—',
            color: codeSmellCount != null
              ? (codeSmellCount === 0 ? '#22c55e' : codeSmellCount <= 10 ? '#f59e0b' : '#ef4444')
              : (theme.palette.text.primary as string),
          },
          {
            kpiId: 'sonar_duplications',
            label: 'Duplications',
            tooltip: 'Percentage of duplicated lines across all files',
            rawVal: dupPct,
            display: dupPct != null ? `${dupPct.toFixed(1)}%` : '—',
            color: dupPct != null
              ? (dupPct <= 3 ? '#22c55e' : dupPct <= 10 ? '#f59e0b' : '#ef4444')
              : (theme.palette.text.primary as string),
          },
          {
            kpiId: 'sonar_debt',
            label: 'Technical Debt',
            tooltip: 'Estimated effort to fix all maintainability issues',
            rawVal: debtVal,
            display: debtVal != null
              ? (typeof debtVal === 'number'
                  ? (debtVal >= 480 ? `${Math.round(debtVal / 480)}d` : debtVal >= 60 ? `${Math.round(debtVal / 60)}h` : `${debtVal}min`)
                  : formatKpiVal(debtVal))
              : '—',
            color: debtVal != null
              ? (typeof debtVal === 'number'
                  ? (debtVal <= 60 ? '#22c55e' : debtVal <= 480 ? '#f59e0b' : '#ef4444')
                  : (theme.palette.text.primary as string))
              : (theme.palette.text.primary as string),
          },
        ];
        return (
          <>
            <Grid container spacing={2} alignItems="stretch">
              {metricWidgets.map((w) => {
                const isActive = activeKpiFilter === w.kpiId;
                return (
                  <Grid key={w.kpiId} item xs={4}>
                    <Box
                      onClick={() => setActiveKpiFilter(isActive ? null : w.kpiId)}
                      sx={{
                        p: 2, borderRadius: 2, height: '100%', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', gap: 0.75,
                        border: '2px solid', borderColor: isActive ? 'primary.main' : 'divider',
                        bgcolor: isActive ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.02) },
                      }}
                    >
                      {/* Label row */}
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>{w.label}</Typography>
                        <Tooltip title={w.tooltip} arrow>
                          <Info size={11} style={{ color: theme.palette.text.disabled, cursor: 'pointer', flexShrink: 0 }} />
                        </Tooltip>
                      </Stack>
                      {/* Metric value */}
                      <Typography sx={{ fontSize: 22, fontWeight: 800, color: w.color, lineHeight: 1.2 }}>
                        {w.display}
                      </Typography>
                      {/* Footer */}
                      <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>click for details</Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
            {/* Detail panel for widget KPIs (sonar_code_smells, sonar_duplications, sonar_debt).
                Use a synthetic 0-100 score derived from the card colour so the panel health bar
                and status badge match the card (green=100, amber=70, red=20). */}
            {activeKpiFilter && ['sonar_code_smells', 'sonar_duplications', 'sonar_debt'].includes(activeKpiFilter) && (() => {
              const activeWidget = metricWidgets.find(w => w.kpiId === activeKpiFilter);
              const syntheticScore = activeWidget?.color === '#22c55e' ? 100
                : activeWidget?.color === '#f59e0b' ? 70
                : 20; // '#ef4444' → critical
              return (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <KpiDetailPanel
                      kpiId={activeKpiFilter}
                      kpiLabel={activeWidget?.label || activeKpiFilter}
                      value={syntheticScore}
                      rawValue={rawValues[activeKpiFilter]}
                      topFixes={topFixes}
                      onClose={() => setActiveKpiFilter(null)}
                    />
                  </Grid>
                </Grid>
              );
            })()}
          </>
        );
      })()}
    </Stack>
  );
}

// ── BlackDuck Tab ─────────────────────────────────────────────
interface BlackDuckTabProps extends ToolTabProps { toolReport?: any; }

export function BlackDuckTab({ toolScore, kpiScores, topFixes, trendData, loading, toolReport, pipelineToolStatus }: BlackDuckTabProps) {
  const theme = useTheme();
  const [activeKpi, setActiveKpi] = useState<string | null>(null);
  if (loading) return <TabSkeleton />;

  const kpiDefs = [
    { id: 'bd_critical', label: 'Critical CVEs', icon: <AlertTriangle size={14} /> },
    { id: 'bd_high', label: 'High CVEs', icon: <Shield size={14} /> },
    { id: 'bd_licence_high', label: 'License Risk (High)', icon: <Scale size={14} /> },
    { id: 'bd_medium', label: 'Medium CVEs', icon: <Bug size={14} /> },
  ];

  // Extract raw counts from tool report so cards show actual numbers not normalised scores
  const rawValues: Record<string, any> = {};
  if (toolReport?.projects) {
    for (const p of toolReport.projects) {
      if (!p.found) continue;
      if (p.vulnerabilities?.critical != null) rawValues['bd_critical'] = p.vulnerabilities.critical;
      if (p.vulnerabilities?.high != null) rawValues['bd_high'] = p.vulnerabilities.high;
      if (p.licenseRisk?.high != null) rawValues['bd_licence_high'] = p.licenseRisk.high;
      if (p.vulnerabilities?.medium != null) rawValues['bd_medium'] = p.vulnerabilities.medium;
    }
  }

  // ── Not-Found guard (mirrors SonarQube) ──────────────────────────────────
  // If every project entry for this repo reports found:false, stale DynamoDB
  // scores must not be shown — display the "not found" empty state instead.
  const repoProjEntriesBd = toolReport?.projects as any[] | undefined;
  if (repoProjEntriesBd?.length && repoProjEntriesBd.every((p: any) => !p.found)) {
    const notFoundMsgBd = repoProjEntriesBd[0]?.message || 'No BlackDuck project found for this repository.';
    return (
      <EmptyState
        icon={<Shield size={18} />}
        title="BlackDuck Project Not Found"
        description={`${notFoundMsgBd} Add or correct the project mapping in the tool settings.`}
      />
    );
  }
  // ─────────────────────────────────────────────────────────────────────────

  const hasData = (toolScore != null && toolScore > 0) || Object.keys(kpiScores).some(k => k.startsWith('bd_'));
  if (!hasData) {
    if (pipelineToolStatus === 'running' || pipelineToolStatus === 'pending' || pipelineToolStatus === 'failed') {
      return <PipelineProgressState toolName="BlackDuck" status={pipelineToolStatus} />;
    }
    // Distinguish "repo has no BlackDuck project" (tool ran, repo not mapped) from "tool not connected"
    const repoNotMapped = pipelineToolStatus === 'completed' || toolReport?.projects?.length === 0;
    return <EmptyState icon={<Shield size={18} />}
      title={repoNotMapped ? 'No BlackDuck Project for this Repository' : 'BlackDuck Not Connected'}
      description={repoNotMapped
        ? 'This repository has no BlackDuck project configured. Switch to another repository to see BlackDuck data, or add a mapping in the tool settings.'
        : 'No BlackDuck data available. Configure it in the onboarding wizard.'} />;
  }

  const filteredFixes = activeKpi ? topFixes.filter(f => f.kpi === activeKpi) : [];

  return (
    <ToolTabContent2
      toolName="BlackDuck"
      toolScore={toolScore}
      icon={<Shield size={14} />}
      color="#1976d2"
      kpiDefs={kpiDefs}
      kpiScores={kpiScores}
      rawValues={Object.keys(rawValues).length > 0 ? rawValues : undefined}
      topFixes={topFixes}
      trendData={trendData || []}
      activeKpi={activeKpi}
      setActiveKpi={setActiveKpi}
      filteredFixes={filteredFixes}
      theme={theme}
    />
  );
}

// ── GitLeaks Tab ──────────────────────────────────────────────
interface GitLeaksTabProps extends ToolTabProps { toolReport?: any; }

export function GitLeaksTab({ toolScore, kpiScores, topFixes, trendData, loading, toolReport, pipelineToolStatus }: GitLeaksTabProps) {
  const theme = useTheme();
  const [activeKpi, setActiveKpi] = useState<string | null>(null);
  if (loading) return <TabSkeleton />;

  const kpiDefs = [
    { id: 'secrets_detected', label: 'Secrets Detected', icon: <Key size={14} /> },
    { id: 'branch_protected', label: 'Branch Protection', icon: <Shield size={14} /> },
    { id: 'pipeline_success', label: 'Pipeline Success', icon: <CheckCircle2 size={14} /> },
    { id: 'stale_prs', label: 'Stale PRs', icon: <Clock size={14} /> },
  ];

  // Extract raw values from GHE/Gitleaks tool report
  const rawValues: Record<string, any> = {};
  if (toolReport?.projects) {
    for (const p of toolReport.projects) {
      if (!p.found) continue;
      if (p.secretsDetected != null) rawValues['secrets_detected'] = p.secretsDetected;
      if (typeof p.branchProtected === 'boolean') rawValues['branch_protected'] = p.branchProtected;
      if (p.pipelineSuccess != null) rawValues['pipeline_success'] = p.pipelineSuccess;
      if (p.stalePrs != null) rawValues['stale_prs'] = p.stalePrs;
    }
  }

  const hasData = (toolScore != null && toolScore > 0) || Object.keys(kpiScores).some(k => ['secrets_detected', 'branch_protected', 'pipeline_success', 'stale_prs'].includes(k));
  if (!hasData) {
    if (pipelineToolStatus === 'running' || pipelineToolStatus === 'pending' || pipelineToolStatus === 'failed') {
      return <PipelineProgressState toolName="GitLeaks / GHE" status={pipelineToolStatus} />;
    }
    return <EmptyState icon={<Key size={18} />} title="GitLeaks Not Connected" description="No GitLeaks data available. Configure it in the onboarding wizard." />;
  }

  const filteredFixes = activeKpi ? topFixes.filter(f => f.kpi === activeKpi) : [];

  return (
    <ToolTabContent2
      toolName="GitLeaks / GHE"
      toolScore={toolScore}
      icon={<Key size={14} />}
      color="#f97316"
      kpiDefs={kpiDefs}
      kpiScores={kpiScores}
      rawValues={Object.keys(rawValues).length > 0 ? rawValues : undefined}
      topFixes={topFixes}
      trendData={trendData || []}
      activeKpi={activeKpi}
      setActiveKpi={setActiveKpi}
      filteredFixes={filteredFixes}
      theme={theme}
    />
  );
}

// ── Trivy Tab ─────────────────────────────────────────────────
export function TrivyTab({ toolScore, kpiScores, topFixes, trendData, loading, toolReport, pipelineToolStatus }: ToolTabProps & { toolReport?: any }) {
  const theme = useTheme();
  const [activeKpi, setActiveKpi] = useState<string | null>(null);
  if (loading) return <TabSkeleton />;

  const kpiDefs = [
    { id: 'image_scan_pass', label: 'Image Scan', icon: <CheckCircle2 size={14} /> },
    { id: 'image_critical_cves', label: 'Critical CVEs', icon: <AlertTriangle size={14} /> },
    { id: 'image_high_cves', label: 'High CVEs', icon: <Shield size={14} /> },
    { id: 'image_age_days', label: 'Image Age', icon: <Clock size={14} />, unit: 'days' },
  ];

  // Extract raw API values from tool report
  const rawValues: Record<string, any> = {};
  const scannedImages: Array<{ repoName: string; imageTag: string; harborRepo?: string; pushedAt?: string; digest?: string; scanPass?: boolean; critical?: number; high?: number }> = [];
  if (toolReport?.projects) {
    for (const p of toolReport.projects) {
      if (!p.found) continue;
      if (typeof p.imageScanPass === 'boolean') rawValues['image_scan_pass'] = p.imageScanPass;
      if (p.imageCriticalCves != null) rawValues['image_critical_cves'] = p.imageCriticalCves;
      if (p.imageHighCves != null) rawValues['image_high_cves'] = p.imageHighCves;
      if (p.imageAgeDays != null) rawValues['image_age_days'] = p.imageAgeDays;
      // Collect scanned image metadata
      if (p.imageTag || p.harborRepo) {
        scannedImages.push({
          repoName: p.repoName,
          imageTag: p.imageTag || 'unknown',
          harborRepo: p.harborRepo,
          pushedAt: p.imagePushedAt,
          digest: p.imageDigest?.substring(0, 19),
          scanPass: p.imageScanPass,
          critical: p.imageCriticalCves,
          high: p.imageHighCves,
        });
      }
    }
  }

  const hasData = (toolScore != null && toolScore > 0) || Object.keys(kpiScores).some(k => k.startsWith('image_'));
  if (!hasData) {
    if (pipelineToolStatus === 'running' || pipelineToolStatus === 'pending' || pipelineToolStatus === 'failed') {
      return <PipelineProgressState toolName="Trivy" status={pipelineToolStatus} />;
    }
    return <EmptyState icon={<Container size={18} />} title="Trivy Not Connected" description="No Trivy data available. Configure it in the onboarding wizard." />;
  }

  const filteredFixes = activeKpi ? topFixes.filter(f => f.kpi === activeKpi) : [];

  return (
    <Stack spacing={2}>
      <ToolTabContent2
        toolName="Trivy"
        toolScore={toolScore}
        icon={<Container size={14} />}
        color="#06b6d4"
        kpiDefs={kpiDefs}
        kpiScores={kpiScores}
        rawValues={Object.keys(rawValues).length > 0 ? rawValues : undefined}
        topFixes={topFixes}
        trendData={trendData}
        activeKpi={activeKpi}
        setActiveKpi={setActiveKpi}
        filteredFixes={filteredFixes}
        theme={theme}
      />
      {/* Scanned Images — wrapped in Grid container so left/right edges match the KPI cards */}
      {scannedImages.length > 0 && (
        <Grid container spacing={2}>
        <Grid item xs={12}>
        <SectionCard title="Scanned Images" icon={<Container size={14} />}>
          <Stack spacing={1}>
            {scannedImages.map((img) => {
              const passColor = img.scanPass ? '#22c55e' : '#ef4444';
              const passLabel = img.scanPass ? 'PASS' : 'FAIL';
              return (
                <Box key={img.repoName} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{img.repoName}</Typography>
                    <Chip label={passLabel} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha(passColor, 0.1), color: passColor, borderRadius: 1 }} />
                    {(img.critical ?? 0) > 0 && <Chip label={`${img.critical} Critical`} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 600, bgcolor: alpha('#ef4444', 0.08), color: '#ef4444', borderRadius: 1 }} />}
                    {(img.high ?? 0) > 0 && <Chip label={`${img.high} High`} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 600, bgcolor: alpha('#f97316', 0.08), color: '#f97316', borderRadius: 1 }} />}
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }} flexWrap="wrap">
                    <Chip icon={<Tag size={10} />} label={img.imageTag} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 500, fontFamily: 'monospace', '& .MuiChip-icon': { fontSize: 10 } }} />
                    {img.harborRepo && <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>repo: {img.harborRepo}</Typography>}
                    {img.digest && <Typography sx={{ fontSize: 10, color: 'text.disabled', fontFamily: 'monospace' }}>{img.digest}</Typography>}
                    {img.pushedAt && <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>pushed {new Date(img.pushedAt).toLocaleDateString()}</Typography>}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </SectionCard>
        </Grid>
        </Grid>
      )}
    </Stack>
  );
}

// ── Shared Tool Tab Layout (Score + Trend + KPIs + Issues) ────
function ToolTabContent2({
  toolName, toolScore, icon, color, kpiDefs, kpiScores, topFixes, trendData,
  activeKpi, setActiveKpi, filteredFixes, theme, rawValues,
}: {
  toolName: string;
  toolScore: number | null;
  icon: React.ReactNode;
  color: string;
  kpiDefs: { id: string; label: string; icon: React.ReactNode; unit?: string }[];
  kpiScores: Record<string, number | string | boolean>;
  topFixes: Array<{ kpi: string; tool: string; action: string; location?: string; effort?: string; impact_points?: number; priority?: string }>;
  trendData?: TrendPoint[];
  activeKpi: string | null;
  setActiveKpi: (kpi: string | null) => void;
  filteredFixes: Array<{ kpi: string; tool: string; action: string; location?: string; effort?: string; impact_points?: number; priority?: string }>;
  theme: any;
  rawValues?: Record<string, number | string | boolean | null>;
}) {
  return (
    <Stack spacing={2}>
      {/* Row 1: Score + Trend */}
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <SectionCard sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Stack alignItems="center" spacing={1}>
              <ScoreWidget score={toolScore} size={90} label={`${toolName} Score`} sublabel={toolScore != null ? (toolScore >= 80 ? 'Good' : toolScore >= 60 ? 'Needs Attention' : 'Critical') : undefined} />
            </Stack>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
          <SectionCard title="Score Trend" icon={icon} sx={{ flex: 1 }}>
            <ScoreTrendChart data={trendData || []} color={color} height={120} title="Last 10 scans" />
          </SectionCard>
        </Grid>
      </Grid>

      {/* Row 2: 4 KPI Cards */}
      <Grid container spacing={2}>
        {kpiDefs.map(kpi => {
          const normScore = kpiScores[kpi.id];
          const rawVal = rawValues?.[kpi.id];
          const score = typeof normScore === 'number' ? normScore : normScore === true ? 100 : normScore === false ? 0 : null;
          const isActive = activeKpi === kpi.id;
          const hasIssues = topFixes.some(f => f.kpi === kpi.id);
          const scoreColor = score != null ? (score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444') : theme.palette.text.disabled;
          // Show raw API value (actual count/bool/%) not the internal 0-100 normalised score
          const displayStr = formatRawKpiValue(kpi.id, rawVal, normScore);
          // For image_age_days the unit chip is shown separately — skip it if already in displayStr
          const showUnit = kpi.unit && kpi.id !== 'image_age_days';
          return (
            <Grid key={kpi.id} item xs={6} md={3}>
              <Box
                onClick={() => setActiveKpi(isActive ? null : kpi.id)}
                sx={{
                  p: 2, borderRadius: 2,
                  border: '2px solid', borderColor: isActive ? 'primary.main' : 'divider',
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                  cursor: 'pointer', transition: 'all 0.15s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.02) },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Box sx={{ color: scoreColor, display: 'flex' }}>{kpi.icon}</Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>{kpi.label}</Typography>
                </Stack>
                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: scoreColor }}>{displayStr}</Typography>
                  {showUnit && <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{kpi.unit}</Typography>}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                  <StatusBadge status={score != null && score >= 80 ? 'success' : score != null && score >= 60 ? 'warning' : 'critical'} label={score != null ? (score >= 80 ? 'Good' : score >= 60 ? 'Warning' : 'Critical') : 'N/A'} />
                  {hasIssues && <Typography sx={{ fontSize: 9, color: 'text.disabled' }}>• details</Typography>}
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Row 3: KPI Detail Panel on KPI click */}
      {activeKpi && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <KpiDetailPanel
              kpiId={activeKpi}
              kpiLabel={kpiDefs.find(k => k.id === activeKpi)?.label || activeKpi}
              value={kpiScores[activeKpi]}
              rawValue={rawValues?.[activeKpi]}
              topFixes={topFixes}
              onClose={() => setActiveKpi(null)}
            />
          </Grid>
        </Grid>
      )}
    </Stack>
  );
}

// ── Delta AI KPIs Tab ─────────────────────────────────────────
export function DeltaAiKpisTab({ kpiScores, topFixes, loading, scanning, scanProgress }: ToolTabProps) {
  const theme = useTheme();
  const [activeKpi, setActiveKpi] = useState<string | null>(null);
  if (loading) return <TabSkeleton />;

  const kpiDefs = [
    { id: 'epss_score', label: 'EPSS Score', icon: <Gauge size={14} /> },
    { id: 'cisa_kev_match_count', label: 'CISA KEV Matches', icon: <AlertTriangle size={14} /> },
    { id: 'reachable_vulnerable_functions', label: 'Reachable Vulns', icon: <Bug size={14} /> },
    { id: 'unreachable_cve_pct', label: 'Unreachable CVE %', icon: <Shield size={14} /> },
    { id: 'mttr_critical', label: 'MTTR Critical', icon: <Clock size={14} /> },
    { id: 'security_regression_rate', label: 'Regression Rate', icon: <AlertTriangle size={14} /> },
    { id: 'sla_breach_count', label: 'SLA Breaches', icon: <CheckCircle2 size={14} /> },
    { id: 'sbom_coverage_pct', label: 'SBOM Coverage', icon: <Scale size={14} /> },
    { id: 'transitive_dependency_depth', label: 'Dependency Depth', icon: <Container size={14} /> },
    { id: 'dependency_confusion_risk_score', label: 'Dep. Confusion Risk', icon: <Key size={14} /> },
  ];

  const hasData = Object.keys(kpiScores).some(k => kpiDefs.some(d => d.id === k));
  const isScanning = scanning || (scanProgress && scanProgress.status !== 'completed' && scanProgress.status !== 'failed');
  if (!hasData && isScanning) {
    const progressPct = scanProgress ? Math.round((scanProgress.passes_completed / scanProgress.passes_total) * 100) : 0;
    const progressLabel = scanProgress
      ? `Pass ${scanProgress.passes_completed}/${scanProgress.passes_total}${scanProgress.current_pass ? ` — ${scanProgress.current_pass.replace('_', ' ')}` : ''}${scanProgress.elapsed_seconds ? ` (${scanProgress.elapsed_seconds}s)` : ''}`
      : 'Initializing scan...';
    return (
      <EmptyState
        icon={<CircularProgress size={24} thickness={4} />}
        title="AI Agent Scan in Progress"
        description={
          <Stack spacing={1} alignItems="center">
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{progressLabel}</Typography>
            {scanProgress && (
              <Box sx={{ width: '60%', minWidth: 180 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressPct}
                  sx={{ height: 4, borderRadius: 2, bgcolor: alpha('#8b5cf6', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#8b5cf6' } }}
                />
              </Box>
            )}
          </Stack>
        }
      />
    );
  }
  if (!hasData) return <EmptyState icon={<Gauge size={18} />} title="AI Agent KPIs Not Available" description="No AI Agent KPI data available. Run a scan to generate insights." />;

  const filteredFixes = activeKpi ? topFixes.filter(f => f.kpi === activeKpi) : [];

  return (
    <Stack spacing={2}>
      {/* KPI Cards — 5 per row on desktop, 2 on mobile */}
      <Grid container spacing={2}>
        {kpiDefs.map(kpi => {
          const raw = kpiScores[kpi.id];
          const score = typeof raw === 'number' ? raw : raw === true ? 100 : raw === false ? 0 : null;
          const isActive = activeKpi === kpi.id;
          const kpiFixes = topFixes.filter(f => f.kpi === kpi.id);
          const hasIssues = kpiFixes.length > 0;
          const priority = kpiFixes[0]?.priority;
          const priorityColor = priority === 'critical' ? '#ef4444' : priority === 'high' ? '#f97316' : priority === 'medium' ? '#f59e0b' : '#3b82f6';
          const scoreColor = score != null ? (score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444') : theme.palette.text.disabled;
          return (
            <Grid key={kpi.id} item xs={6} sm={4} md={2.4}>
              <Box
                onClick={() => setActiveKpi(isActive ? null : kpi.id)}
                sx={{
                  p: 1.5, borderRadius: 2,
                  border: '2px solid', borderColor: isActive ? 'primary.main' : 'divider',
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                  cursor: 'pointer', transition: 'all 0.15s', height: '100%',
                  '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.02) },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                  <Box sx={{ color: scoreColor, display: 'flex' }}>{kpi.icon}</Box>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary', lineHeight: 1.2 }}>{kpi.label}</Typography>
                </Stack>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: scoreColor }}>{formatKpiVal(raw)}</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                  {priority && (
                    <Chip label={priority} size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: alpha(priorityColor, 0.1), color: priorityColor, borderRadius: 1, textTransform: 'capitalize' }} />
                  )}
                  {hasIssues && <Typography sx={{ fontSize: 8, color: 'text.disabled' }}>click for details</Typography>}
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* KPI Detail Panel on KPI click */}
      {activeKpi && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <KpiDetailPanel
              kpiId={activeKpi}
              kpiLabel={kpiDefs.find(k => k.id === activeKpi)?.label || activeKpi}
              value={kpiScores[activeKpi]}
              topFixes={topFixes}
              onClose={() => setActiveKpi(null)}
            />
          </Grid>
        </Grid>
      )}
    </Stack>
  );
}

// ── Tab Skeleton ──────────────────────────────────────────────
function TabSkeleton() {
  return (
    <Stack spacing={3}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}><KpiCardSkeleton /></Grid>
        <Grid item xs={12} md={8}><KpiCardSkeleton /></Grid>
      </Grid>
      <Grid container spacing={2}>
        {[1, 2, 3, 4].map((i) => (
          <Grid key={i} item xs={12} sm={6}><KpiCardSkeleton /></Grid>
        ))}
      </Grid>
    </Stack>
  );
}
